import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { onboardingService, clinicService } from '../services/api';
import {
  OnboardingHeader,
  ProgressSteps,
  Step1_ClinicSelection,
  Step2_WabaIdInput,
  Step3_PhoneNumberSelection,
  Step4_Confirmation
} from '../components/Onboarding';

const Onboarding = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [selectedClinic, setSelectedClinic] = useState(null);
  const [wabaId, setWabaId] = useState('');
  const [phoneNumbers, setPhoneNumbers] = useState([]);
  const [selectedPhoneNumber, setSelectedPhoneNumber] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [tokenName, setTokenName] = useState('');
  const [tokenLocale, setTokenLocale] = useState('en_US');
  const [creatingToken, setCreatingToken] = useState(false);
  const [createdToken, setCreatedToken] = useState('');
  const [createdTokenUrl, setCreatedTokenUrl] = useState('');
  const [tokenError, setTokenError] = useState('');

  const handleClinicSelect = (clinic) => {
    setSelectedClinic(clinic);
    setTokenName(clinic?.name || '');
    setTokenLocale(clinic?.region === 'HK' ? 'zh_HK' : 'en_US');
    setCreatedToken('');
    setCreatedTokenUrl('');
    setTokenError('');
    setError('');
  };

  const handleClinicCreate = async (clinicData) => {
    try {
      console.log('Creating new clinic:', clinicData);
      const response = await clinicService.createClinic(clinicData);
      console.log('Clinic created:', response);
      return response;
    } catch (err) {
      console.error('Error creating clinic:', err);
      setError(err.response?.data?.error || err.message || 'An error occurred while creating clinic');
      return null;
    }
  };

  const handleCreateToken = async () => {
    if (!selectedClinic?.id) {
      setTokenError('Please select a clinic first');
      return;
    }
    if (!tokenName.trim()) {
      setTokenError('Please provide onboarding display name');
      return;
    }

    setCreatingToken(true);
    setTokenError('');
    try {
      const response = await onboardingService.createOnboardingToken({
        clid: selectedClinic.id,
        name: tokenName.trim(),
        locale: tokenLocale,
        type: 'clinic',
      });
      if (response?.success) {
        setCreatedToken(response.token || '');
        setCreatedTokenUrl(response.onboarding_url || '');
      } else {
        setTokenError(response?.message || 'Failed to create onboarding token');
      }
    } catch (err) {
      setTokenError(err.response?.data?.detail || err.message || 'Failed to create onboarding token');
    } finally {
      setCreatingToken(false);
    }
  };

  const handleCopyTokenUrl = async () => {
    if (!createdTokenUrl) return;
    try {
      await navigator.clipboard.writeText(createdTokenUrl);
      alert('Onboarding URL copied to clipboard');
    } catch (err) {
      setTokenError('Failed to copy URL');
    }
  };

  const handleClinicNext = () => {
    if (selectedClinic) {
      setCurrentStep(1);
      setError('');
    }
  };

  const handleWabaIdSubmit = async () => {
    if (!wabaId.trim()) {
      setError('Please enter a WABA ID');
      return;
    }

    setLoading(true);
    setError('');

    try {
      console.log('Fetching phone numbers for WABA ID:', wabaId);
      const response = await onboardingService.getPhoneNumbersByWabaId(wabaId);

      if (response.success && response.data) {
        console.log('Phone numbers received:', response.data);
        setPhoneNumbers(response.data);
        setCurrentStep(2);
      } else {
        setError(response.error || 'Failed to fetch phone numbers');
      }
    } catch (err) {
      console.error('Error fetching phone numbers:', err);
      setError(err.response?.data?.error || err.message || 'An error occurred while fetching phone numbers');
    } finally {
      setLoading(false);
    }
  };

  const handlePhoneNumberSelect = (phoneNumberId) => {
    setSelectedPhoneNumber(phoneNumberId);
    setCurrentStep(3); // Move to final step after phone number selection
  };

  const handleCompleteOnboarding = async (onboardingData) => {
    if (!onboardingData || !onboardingData.phone_number_id) {
      setError('Please select a phone number');
      return;
    }

    setLoading(true);
    setError('');

    try {
      console.log('Completing onboarding with data:', onboardingData);
      const response = await onboardingService.completeOnboarding(
        onboardingData.waba_id,
        onboardingData.phone_number_id,
        onboardingData.phone_number_name,
        onboardingData.phone_number,
        onboardingData.clid
      );

      if (response.success) {
        console.log('Onboarding completed successfully');
        // Redirect to clinics page after successful completion
        alert('Onboarding completed successfully! Redirecting to clinics...');
        setTimeout(() => {
          navigate('/clinics');
        }, 1500);
      } else {
        setError(response.error || 'Failed to complete onboarding');
      }
    } catch (err) {
      console.error('Error completing onboarding:', err);
      setError(err.response?.data?.error || err.message || 'An error occurred while completing onboarding');
    } finally {
      setLoading(false);
    }
  };

  const goToStep = (step) => {
    setCurrentStep(step);
    setError('');
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="bg-white rounded-lg shadow-lg p-8">
        <OnboardingHeader />
        <ProgressSteps currentStep={currentStep} totalSteps={4} />

        <div className="mb-6 rounded-lg border border-gray-200 bg-gray-50 p-4">
          <h3 className="text-md font-semibold mb-3">Create token-based onboarding link</h3>
          <p className="text-sm text-gray-600 mb-4">
            Generate a link for <code>/onboarding?token=...</code> flow used by clinic users.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Clinic</label>
              <input
                type="text"
                value={selectedClinic?.id || ''}
                placeholder="Select clinic in Step 1"
                disabled
                className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-700"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Display Name</label>
              <input
                type="text"
                value={tokenName}
                onChange={(e) => setTokenName(e.target.value)}
                placeholder="Clinic display name"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Locale</label>
              <select
                value={tokenLocale}
                onChange={(e) => setTokenLocale(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
              >
                <option value="en_US">en_US</option>
                <option value="zh_HK">zh_HK</option>
              </select>
            </div>
          </div>
          <div className="mt-4 flex items-center gap-2">
            <button
              onClick={handleCreateToken}
              disabled={creatingToken || !selectedClinic?.id}
              className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {creatingToken ? 'Creating...' : 'Create Onboarding Token'}
            </button>
            {createdTokenUrl && (
              <button
                onClick={handleCopyTokenUrl}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                Copy URL
              </button>
            )}
          </div>
          {createdToken && (
            <div className="mt-3 text-sm">
              <p><span className="font-medium">Token:</span> {createdToken}</p>
              <p className="break-all"><span className="font-medium">URL:</span> {createdTokenUrl}</p>
            </div>
          )}
          {tokenError && (
            <div className="mt-3 text-sm text-red-600">{tokenError}</div>
          )}
        </div>

        {/* Step 1: Clinic Selection/Creation */}
        {currentStep === 0 && (
          <Step1_ClinicSelection
            selectedClinic={selectedClinic}
            onClinicSelect={handleClinicSelect}
            onClinicCreate={handleClinicCreate}
            onNext={handleClinicNext}
            loading={loading}
            error={error}
            setError={setError}
          />
        )}

        {/* Step 1: WABA ID Input */}
        {currentStep === 1 && (
          <Step2_WabaIdInput
            wabaId={wabaId}
            setWabaId={setWabaId}
            onSubmit={handleWabaIdSubmit}
            onBack={() => goToStep(0)}
            loading={loading}
            error={error}
          />
        )}

        {/* Step 2: Phone Number Selection */}
        {currentStep === 2 && (
          <Step3_PhoneNumberSelection
            phoneNumbers={phoneNumbers}
            selectedPhoneNumber={selectedPhoneNumber}
            onPhoneNumberSelect={handlePhoneNumberSelect}
            onBack={() => goToStep(1)}
            loading={loading}
            error={error}
            wabaId={wabaId}
            clinicId={selectedClinic?.id}
          />
        )}

        {/* Step 4: Complete Onboarding */}
        {currentStep === 3 && (
          <Step4_Confirmation
            selectedClinic={selectedClinic}
            wabaId={wabaId}
            phoneNumbers={phoneNumbers}
            selectedPhoneNumber={selectedPhoneNumber}
            onBack={() => goToStep(2)}
            onComplete={handleCompleteOnboarding}
            loading={loading}
            error={error}
          />
        )}
      </div>
    </div>
  );
};

export default Onboarding;
